import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=user
  const [authOpen, setAuthOpen] = useState(false);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("aox_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("aox_token");
      setUser(false);
    }
  }, []);

  const handleGoogleCallback = useCallback(async (hash) => {
    try {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      const { data } = await api.post(
        "/auth/google/session",
        {},
        { headers: { "X-Session-ID": sid } }
      );
      localStorage.setItem("aox_token", data.session_token);
      setUser(data.user);
    } catch {
      setUser(false);
    } finally {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      handleGoogleCallback(hash);
    } else {
      refresh();
    }
  }, [refresh, handleGoogleCallback]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("aox_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      localStorage.setItem("aox_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("aox_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, login, register, loginWithGoogle, logout, refresh, authOpen, setAuthOpen }}
    >
      {children}
    </AuthContext.Provider>
  );
}
